import React, { useState, useEffect } from 'react';
import { Container, Button } from '../design-system';
import { Menu, X } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { trackEvent } from '../utils/analytics';

interface HeaderProps {
  onContactClick: () => void;
}

export function Header({ onContactClick }: HeaderProps) {
  const { t, setLang, region, setRegion } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.features'), href: '#features' },
    { label: t('nav.process'), href: '#process' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.faq'), href: '#faq' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <Container maxWidth="xl">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <a href="#home" aria-label="Gruty">
              <img
                src="/images/logo.png"
                alt="Gruty"
                className="h-12 w-auto"
                decoding="async"
                fetchPriority="high"
              />
            </a>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-slate-700 hover:text-indigo-600 transition-colors font-medium"
                onClick={() => trackEvent('nav_click', { section: item.href.replace('#', '') })}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1">
              <button
                type="button"
                onClick={() => { setLang('pt-BR'); setRegion('br'); trackEvent('language_change', { to: 'pt-BR', region: 'br' }); }}
                aria-label="Português (Brasil)"
                title="Português (Brasil)"
                className={`px-2 py-1 rounded-md ${region === 'br' ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
              >
                <span className="fi fi-br" />
              </button>
              <button
                type="button"
                onClick={() => { setLang('en'); setRegion('us'); trackEvent('language_change', { to: 'en', region: 'us' }); }}
                aria-label="English (US)"
                title="English (US)"
                className={`px-2 py-1 rounded-md ${region === 'us' ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
              >
                <span className="fi fi-us" />
              </button>
              <button
                type="button"
                onClick={() => { setLang('en'); setRegion('ca'); trackEvent('language_change', { to: 'en', region: 'ca' }); }}
                aria-label="English (Canada)"
                title="English (Canada)"
                className={`px-2 py-1 rounded-md ${region === 'ca' ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
              >
                <span className="fi fi-ca" />
              </button>
            </div>
            <Button onClick={onContactClick}>
              {t('buttons.contact')}
            </Button>
          </div>

          <button
            className="md:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            type="button"
            aria-label={isMobileMenuOpen ? 'Fechar menu de navegação' : 'Abrir menu de navegação'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div id="mobile-navigation" className="md:hidden py-4 border-t border-slate-200">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-slate-700 hover:text-indigo-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setLang('pt-BR'); setRegion('br'); trackEvent('language_change', { to: 'pt-BR', region: 'br' }); }}
                  aria-label="Português (Brasil)"
                  title="Português (Brasil)"
                  className={`flex-1 p-2 rounded-lg border border-slate-200 hover:bg-slate-100 ${region === 'br' ? 'bg-slate-100' : ''}`}
                >
                  <span className="fi fi-br" />
                </button>
                <button
                  type="button"
                  onClick={() => { setLang('en'); setRegion('us'); trackEvent('language_change', { to: 'en', region: 'us' }); }}
                  aria-label="English (US)"
                  title="English (US)"
                  className={`flex-1 p-2 rounded-lg border border-slate-200 hover:bg-slate-100 ${region === 'us' ? 'bg-slate-100' : ''}`}
                >
                  <span className="fi fi-us" />
                </button>
                <button
                  type="button"
                  onClick={() => { setLang('en'); setRegion('ca'); trackEvent('language_change', { to: 'en', region: 'ca' }); }}
                  aria-label="English (Canada)"
                  title="English (Canada)"
                  className={`flex-1 p-2 rounded-lg border border-slate-200 hover:bg-slate-100 ${region === 'ca' ? 'bg-slate-100' : ''}`}
                >
                  <span className="fi fi-ca" />
                </button>
              </div>
              <Button 
                onClick={() => {
                  onContactClick();
                  setIsMobileMenuOpen(false);
                  trackEvent('cta_click', { location: 'header' });
                }}
                fullWidth
              >
                {t('buttons.contact')}
              </Button>
            </nav>
          </div>
        )}
      </Container>
    </header>
  );
}
